// Angular
import { BrowserModule } from '@angular/platform-browser';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OverlayModule } from '@angular/cdk/overlay';
// Angular in memory
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
// NgBootstrap
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
// SVG inline
import { InlineSVGModule } from 'ng-inline-svg-2';
// Env
import { environment } from '../environments/environment';
// NGX Permissions
import { NgxPermissionsModule } from 'ngx-permissions';
// NGRX
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule, FullRouterStateSerializer } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
// State
import { metaReducers, reducers } from './core/reducers';
// Copmponents
import { AppComponent } from './app.component';
// Modules
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
// Partials
import { PartialsModule } from './views/partials/partials.module';
// Metronic Services
import { DataTableService, FakeApiService } from './core/_base/metronic';
// Layout Services
import {
	LayoutConfigService, LayoutRefService, MenuAsideService, MenuConfigService, MenuHorizontalService, PageConfigService, SplashScreenService, SubheaderService,
	KtDialogService
} from './core/_base/layout';
// Auth
import { AuthModule } from './views/pages/auth/auth.module';
import { AuthService, PermissionEffects, permissionsReducer, RoleEffects, rolesReducer } from './core/auth';
// CRUD
import { HttpUtilsService, LayoutUtilsService, TypesUtilsService } from './core/_base/crud';
// Config
import { LayoutConfig } from './core/_config/demo2/layout.config';
// Highlight JS
import { HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import { DatePipe } from '@angular/common';
import { UserService } from './core/auth/_services/user.service';
import { SettingsService } from './views/pages/configuration/settings/settings.service';
import { setSettingsServiceInstance } from './core/alert/alert-module.loader';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApiPrefixInterceptor } from './core/_base/layout/services/api-prefix-interceptor.service';
import { GlobalErrorHandler } from './core/global-error-handler';

// import { LightboxModule } from 'ngx-lightbox';

export function initializeLayoutConfig(appConfig: LayoutConfigService, userService: UserService, settingsService: SettingsService) {
	// initialize app by loading default demo layout config
	return async () => {
		if (appConfig.getConfig() === null) {
			appConfig.loadConfigs(new LayoutConfig().configs);
		}
		userService.setUpUserActiveStatusCheck();
		userService.checkUserInactiveState();
		// if the auth token is present in local storage, only then fetch all page settings
		const token = localStorage.getItem(environment.runtime.authTokenKey);
		if (token) {
			settingsService.getAllPageSettings();
			await settingsService.waitForSettings();
			// get-public-info populates critical pre-login fields (e.g. authTokenKey).
			// We merge to preserve them because for newly created tenants, the
			// authenticated fetch returns an empty config which would wipe the runtime.
			environment.runtime = { ...environment.runtime, ...settingsService.extractEnvironmentConfig() };
		}
		// Set the service instance for alert module loader
		setSettingsServiceInstance(settingsService);
	};
}

export function hljsLanguages() {
	return {
		typescript: () => import('highlight.js/lib/languages/typescript'),
		scss: () => import('highlight.js/lib/languages/scss'),
		xml: () => import('highlight.js/lib/languages/xml'),
		json: () => import('highlight.js/lib/languages/json')
	};
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		DragDropModule,
		BrowserAnimationsModule,
		BrowserModule,
		AppRoutingModule,
		HttpClientModule,
		environment.isMockEnabled ? HttpClientInMemoryWebApiModule.forRoot(FakeApiService, {
			passThruUnknownUrl: true,
			dataEncapsulation: false
		}) : [],
		NgxPermissionsModule.forRoot(),
		PartialsModule,
		CoreModule,
		OverlayModule,
		StoreModule.forRoot(reducers, { metaReducers, runtimeChecks: { strictStateImmutability: false, strictActionImmutability: false } }),
		EffectsModule.forRoot([]),
		StoreRouterConnectingModule.forRoot({ serializer: FullRouterStateSerializer, stateKey: 'router' }),
		StoreDevtoolsModule.instrument(),
		AuthModule.forRoot(),
		NgbModule,
		TranslateModule.forRoot(),
		MatProgressSpinnerModule,
		InlineSVGModule.forRoot(),
		// LightboxModule,
	],
	exports: [],
	providers: [
		AuthService,
		LayoutConfigService,
		LayoutRefService,
		MenuConfigService,
		PageConfigService,
		KtDialogService,
		DataTableService,
		SplashScreenService,
		{
			// layout config initializer
			provide: APP_INITIALIZER,
			useFactory: initializeLayoutConfig,
			deps: [LayoutConfigService, UserService, SettingsService],
			multi: true
		},
		{
			provide: HIGHLIGHT_OPTIONS,
			useValue: { languages: hljsLanguages }
		},
		// template services
		SubheaderService,
		MenuHorizontalService,
		MenuAsideService,
		HttpUtilsService,
		TypesUtilsService,
		LayoutUtilsService,
		DatePipe,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ApiPrefixInterceptor,
			multi: true
		},
		{
			provide: ErrorHandler,
			useClass: GlobalErrorHandler
		},
	],
	bootstrap: [AppComponent]
})
export class AppModule {
}
